import { Component, effect, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CementProduct,
  cementProductNombrePublicacionSelectLabel,
} from 'src/app/models/cement-product';
import { CementoCheckoutState, CementoProductoSelection } from 'src/app/models/cemento-checkout';
import { CementoService } from 'src/app/services/cemento.service';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { findMatchingRecoveredProduct } from 'src/app/shared/utils/cemento-cotizacion.mapper';

@Component({
  selector: 'app-cemento-product-section',
  templateUrl: './cemento-product-section.component.html',
  styleUrls: ['./cemento-product-section.component.css'],
})
export class CementoProductSectionComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cementoService = inject(CementoService);
  private readonly flow = inject(CementoCheckoutFlowService);
  private readonly checkoutState = toSignal(this.flow.checkoutState, {
    initialValue: {} as CementoCheckoutState,
  });

  readonly validChange = output<boolean>();

  form!: FormGroup;
  readonly products = signal<CementProduct[]>([]);

  constructor() {
    this.initForm();
    effect(() => {
      const dispatchAddress = this.checkoutState()?.mapa?.dispatchAddress;
      this.loadProducts(dispatchAddress?.comuna, dispatchAddress?.regionCode);
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      product: [null as CementProduct | null, [Validators.required]],
    });

    this.form.statusChanges.subscribe(() => {
      this.validChange.emit(this.form.valid);
    });
    this.validChange.emit(this.form.valid);
  }

  private loadProducts(comuna?: string, regionCode?: number): void {
    if (!comuna || regionCode == null) {
      this.products.set([]);
      return;
    }

    this.cementoService.getProducts({
      comuna,
      region: regionCode,
    }).subscribe({
      next: (data) =>
        this.products.set(
          data.map((product) => {
            const p = product as CementProduct;
            return {
              ...p,
              nombre_publicacion_label:
                cementProductNombrePublicacionSelectLabel(p),
            };
          })
        ),
      error: () => this.products.set([]),
    });
  }

  private readonly syncRecoveredProduct = effect(() => {
    const control = this.form?.get('product');
    const selected = this.flow.checkoutStateValue.producto?.product;
    const matched = findMatchingRecoveredProduct(this.products(), selected);
    if (!control || !matched) {
      return;
    }
    if ((control.value as CementProduct | null)?.id !== matched.id) {
      control.setValue(matched, { emitEvent: false });
      this.validChange.emit(this.form.valid);
    }
  });

  getSelection(): CementoProductoSelection | null {
    if (!this.form.valid) {
      return null;
    }
    const product = this.form.get('product')?.value as CementProduct | null;
    if (!product) {
      return null;
    }
    return { product };
  }
}
